/**
 * The wording and rules behind a Player's broadcast email switches - "Emails from Managers" in
 * Account Settings and one switch per Manager on the My Managers tab - and the unsubscribe page a
 * broadcast email links to (/Unsubscribe?token=...), kept pure so they can be tested on their own.
 *
 * Only Broadcasting Center emails heed the switches, and everything is on until the player turns it
 * off. The server applies them: a player who turns emails off is never emailed, a broadcast still
 * waiting included. "Subscribe" already means a request to a Manager (and the paid plan), so none of
 * this wording uses it for emails.
 */

export const ALL_EMAILS_LABEL = "Emails from Managers";
export const ALL_EMAILS_HINT =
  "Announcements your Managers send you by email. Turn this off to stop every Manager's emails, or turn off one Manager on the Managers page.";
export const ALL_EMAILS_OFF_TOOLTIP = "Emails from Managers are off in Account Settings";

/** The My Managers tab's switch label, "Emails from Mike R.". */
export const managerEmailsLabel = (name) => `Emails from ${name || "this manager"}`;

/** What the unsubscribe page shows. */
export const UNSUBSCRIBE_STATE = {
  LOADING: "loading",
  /** The link is not ours, or its account is no longer emailed. */
  INVALID: "invalid",
  /** The Manager's emails are on: both choices are offered. */
  ASK: "ask",
  /** This Manager's emails are already off: only "Stop all" is left to offer. */
  MANAGER_OFF: "managerOff",
  /** The player has left this Manager, so no switch exists for them: only "Stop all". */
  NOT_MANAGERS_PLAYER: "notManagersPlayer",
  /** Every Manager's emails are off: nothing is left to do. */
  ALL_OFF: "allOff",
  /** The player has just turned emails off here. */
  DONE: "done",
};

/**
 * The page's state from Unsubscribe/Details.
 *
 * @param {{status?: boolean, managerEmails?: boolean|null, allEmails?: boolean}|null|undefined} details
 * @returns {string} One of UNSUBSCRIBE_STATE, never LOADING or DONE.
 */
export const unsubscribeState = (details) => {
  if (details?.status !== true) return UNSUBSCRIBE_STATE.INVALID;
  if (details.allEmails === false) return UNSUBSCRIBE_STATE.ALL_OFF;
  if (details.managerEmails === null || details.managerEmails === undefined) return UNSUBSCRIBE_STATE.NOT_MANAGERS_PLAYER;
  if (details.managerEmails === false) return UNSUBSCRIBE_STATE.MANAGER_OFF;
  return UNSUBSCRIBE_STATE.ASK;
};

/** Whether the page offers "Stop emails from <Manager>". */
export const canStopManager = (state) => state === UNSUBSCRIBE_STATE.ASK;

/** Whether the page offers "Stop all Manager emails". */
export const canStopAll = (state) =>
  state === UNSUBSCRIBE_STATE.ASK ||
  state === UNSUBSCRIBE_STATE.MANAGER_OFF ||
  state === UNSUBSCRIBE_STATE.NOT_MANAGERS_PLAYER;

export const stopManagerLabel = (name) => `Stop emails from ${name || "this manager"}`;
export const STOP_ALL_LABEL = "Stop all Manager emails";

/**
 * The page's heading and sentence for a state.
 *
 * @param {string} state One of UNSUBSCRIBE_STATE.
 * @param {string} [managerName] The Manager whose email held the link.
 * @param {boolean} [stoppedAll] For DONE: whether every Manager's emails were turned off.
 * @returns {{title: string, text: string}}
 */
export const unsubscribeText = (state, managerName, stoppedAll = false) => {
  const name = managerName || "this manager";
  switch (state) {
    case UNSUBSCRIBE_STATE.ASK:
      return {
        title: `Stop emails from ${name}?`,
        text: `You'll stay subscribed to ${name} on Xcutta; only the emails stop.`,
      };
    case UNSUBSCRIBE_STATE.MANAGER_OFF:
      return {
        title: `Emails from ${name} are off`,
        text: `You don't get emails from ${name}. You can also stop the emails of every Manager.`,
      };
    case UNSUBSCRIBE_STATE.NOT_MANAGERS_PLAYER:
      return {
        title: `You don't get emails from ${name}`,
        text: `You're no longer one of ${name}'s players, so their emails have stopped. You can also stop the emails of every Manager.`,
      };
    case UNSUBSCRIBE_STATE.ALL_OFF:
      return {
        title: "Emails from Managers are off",
        text: "You don't get emails from any Manager on Xcutta.",
      };
    case UNSUBSCRIBE_STATE.DONE:
      return stoppedAll
        ? { title: "Done", text: "You won't get emails from any Manager on Xcutta any more." }
        : { title: "Done", text: `You won't get emails from ${name} any more. You'll stay subscribed to ${name} on Xcutta.` };
    default:
      return {
        title: "This link isn't valid",
        text: "Make sure you opened the whole link from the email. You can also turn Manager emails off in Account Settings.",
      };
  }
};

/**
 * Where the player turns emails back on: the page itself cannot, so it says which screen can.
 *
 * @param {string} state One of UNSUBSCRIBE_STATE.
 * @param {boolean} [stoppedAll] For DONE: whether every Manager's emails were turned off.
 * @returns {string|null} Null when there is nothing to turn back on.
 */
export const turnBackOnHint = (state, stoppedAll = false) => {
  if (state === UNSUBSCRIBE_STATE.ALL_OFF || (state === UNSUBSCRIBE_STATE.DONE && stoppedAll)) {
    return "To get them again, sign in and turn on Emails from Managers in Account Settings.";
  }
  if (state === UNSUBSCRIBE_STATE.MANAGER_OFF || state === UNSUBSCRIBE_STATE.DONE) {
    return "To get them again, sign in and turn them on in Managers, on the My Managers tab.";
  }
  return null;
};
